import BackendService from 'BackendService';
import React from 'react';
import { ActivityLogExtendedInfo } from '../models/ActivityLog';
import ContentManagementInterface from '../models/ContentManagementInterface';

export interface ExtendedInfo {
  impersonationContext?: {
    impersonatedBy: string;
    impersonatedAt: string;
  };
  [key: string]: any;
}

/**
 * Fetches extended information for activity logs directly from the GCP bucket
 * This saves backend costs by accessing the data directly from storage
 */
export const fetchExtendedInfo = async (
  extendedInfoPath: string,
  backendService: BackendService,
): Promise<ActivityLogExtendedInfo | ContentManagementInterface | null> => {
  if (!extendedInfoPath) {
    return null;
  }

  try {
    // Get the storage token
    const tokenData = await backendService.setupAssetStorageWithRetry(false);

    // Fetch the extended info from the bucket
    const extendedInfoContent = await backendService.getAsset(
      extendedInfoPath,
      tokenData.token.token,
      { url: false },
    );

    if (typeof extendedInfoContent === 'string') {
      return JSON.parse(extendedInfoContent) as
        | ActivityLogExtendedInfo
        | ContentManagementInterface;
    }

    return null;
  } catch (error) {
    console.error('Failed to fetch extended info:', error);
    return null;
  }
};

/**
 * Hook for fetching extended info in React components
 */
export const useExtendedInfo = (
  extendedInfoPath: string | undefined,
  backendService: BackendService,
) => {
  const [extendedInfo, setExtendedInfo] = React.useState<ExtendedInfo | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!extendedInfoPath) {
      setExtendedInfo(null);
      return;
    }

    const loadExtendedInfo = async () => {
      setLoading(true);
      setError(null);

      try {
        const info = await fetchExtendedInfo(extendedInfoPath, backendService);
        setExtendedInfo(info);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load extended info');
      } finally {
        setLoading(false);
      }
    };

    loadExtendedInfo();
  }, [extendedInfoPath, backendService]);

  return { extendedInfo, loading, error };
};
